import { useState, useCallback, useMemo } from "react";
import { AxiosError } from "axios";
import { useAppSnackbar } from "./useAppSnackbar";
import {
  Response,
  masterProfileDisableEnableUpdate,
  masterProfilePrivacyDelete,
  masterProfileDnssUpdate,
  masterProfileLockRemoveUpdate,
  masterProfileRightToKnowReport,
  masterProfileUpdate,
  masterProfileResetPassword,
} from "../api/api";

// =============================================================================
// TYPES
// =============================================================================

/** Supported action keys for Master Profile */
export type MasterProfileActionKey =
  | "updateAccount"
  | "toggleAccountStatus"
  | "privacyDelete"
  | "dnss"
  | "resetPassword"
  | "lockAccount"
  | "rightToKnow";

/** Update account data fields */
export type UpdateAccountData = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
};

/** Format birthday as 1900-MM-DDT00:00:00.000Z since we only store month and day */
function formatBirthday(dateOfBirth?: string): string | undefined {
  if (!dateOfBirth) {
    return undefined;
  }

  let month = "";
  let day = "";

  if (/^\d{2}-\d{2}$/.test(dateOfBirth)) {
    [month, day] = dateOfBirth.split("-");
  } else if (/^\d{4}-\d{2}-\d{2}/.test(dateOfBirth)) {
    [, month, day] = dateOfBirth.split("-");
    day = day.substring(0, 2);
  } else {
    return dateOfBirth;
  }

  return `1900-${month}-${day}T00:00:00.000Z`;
}

/** Payload shape for Master Profile actions. */
export type ActionPayload = {
  mpId?: string;
  cognitoId?: string;
  email?: string;
  status?: string;
  /** DNSS gpcSignal value */
  gpcSignal?: boolean;
  customerName?: string;
  /** Current account enabled status - used for toggle */
  isAccountEnabled?: boolean;
  /** Zaxbys ID for lock account */
  zaxbysId?: string;
  updateData?: UpdateAccountData;
  [key: string]: unknown;
};

/** Normalized response shape from API calls. */
export type ActionResponse = {
  status: number;
  data?: Response<unknown>;
};

/** Shape for errored items from 206 responses. */
export type ErrorItem =
  | { id?: string; name?: string; error?: string; [key: string]: unknown }
  | string;

/** Configuration for a single action */
export type ActionConfig = {
  key: MasterProfileActionKey;
  label: string;
  description: string;
  confirmText?: string;
  apiCall: (payload: ActionPayload) => Promise<ActionResponse>;
};

/** State for the currently selected action */
export type CurrentAction = {
  key: MasterProfileActionKey;
  label: string;
  description: string;
  confirmText: string;
} | null;

/** Formatted error for display */
export type FormattedError = {
  displayName: string;
  errorMessage?: string;
};

// =============================================================================
// API FUNCTIONS
// =============================================================================

/** Update account with user details (firstName, lastName, email, phoneNumber, dateOfBirth). */
async function updateAccountApi(payload: ActionPayload): Promise<ActionResponse> {
  if (!payload.mpId) {
    return { status: 400, data: { status: 400, value: null, error: "Missing mpId", message: null, refreshToken: null } };
  }
  if (!payload.updateData) {
    return { status: 400, data: { status: 400, value: null, error: "Missing update data", message: null, refreshToken: null } };
  }
  
  // Format birthday to MM-DD (only month and day)
  const formattedBirthday = formatBirthday(payload.updateData.dateOfBirth);
  
  // Build updateValues object, only including non-empty values
  const updateValues: Record<string, unknown> = {};
  
  const firstName = payload.updateData.firstName?.trim();
  const lastName = payload.updateData.lastName?.trim();
  const email = payload.updateData.email?.trim();
  const phone = payload.updateData.phoneNumber?.trim();
  
  if (firstName && firstName !== "string") { updateValues.firstName = firstName; }
  if (lastName && lastName !== "string") { updateValues.lastName = lastName; }
  if (email && email !== "string") { updateValues.email = email; }
  if (phone && phone !== "string") { updateValues.mobilePhoneNumber = phone; }
  if (formattedBirthday) {
    updateValues.birthday = formattedBirthday;
    updateValues.birthdayUpdated = true;
  }
  
  // Check if there's anything to update
  if (Object.keys(updateValues).length === 0) {
    return { status: 400, data: { status: 400, value: null, error: "No valid fields to update", message: null, refreshToken: null } };
  }
  
  // Try simpler format without nested request wrapper
  const requestBody = { updateValues };
  
  const data = await masterProfileUpdate(payload.mpId, requestBody as any);
  return { status: data.status ?? 200, data };
}


/** Toggle account status (enable/disable) using existing API function. */
async function toggleAccountStatusApi(payload: ActionPayload): Promise<ActionResponse> {
  if (!payload.mpId) {
    return { status: 400, data: { status: 400, value: null, error: "Missing mpId", message: null, refreshToken: null } };
  }
  
  // Backend logic: status: true = DISABLE, status: false = ENABLE
  // So if account is currently enabled, send true to disable it
  // If account is currently disabled, send false to enable it
  const statusToSend = payload.isAccountEnabled;
  
  const requestBody = { 
    status: statusToSend 
  };
  
  const data = await masterProfileDisableEnableUpdate(payload.mpId, requestBody);
  return { status: data.status ?? 200, data };
}

/** Privacy delete using existing API function. */
async function privacyDeleteApi(payload: ActionPayload): Promise<ActionResponse> {
  const data = await masterProfilePrivacyDelete(payload.mpId);
  return { status: data.status ?? 200, data };
}

/** DNSS update using existing API function. */
async function dnssApi(payload: ActionPayload): Promise<ActionResponse> {
  if (!payload.mpId) {
    return { status: 400, data: { status: 400, value: null, error: "Missing mpId", message: null, refreshToken: null } };
  }
  const data = await masterProfileDnssUpdate(payload.mpId, { 
    gpcSignal: payload.gpcSignal ?? true,
  });
  return { status: data.status ?? 200, data };
}

/** Admin-initiated password reset - sends reset email to user. */
async function resetPasswordApi(payload: ActionPayload): Promise<ActionResponse> {
  if (!payload.cognitoId) {
    return { status: 400, data: { status: 400, value: null, error: "Missing cognitoId", message: null, refreshToken: null } };
  }
  const data = await masterProfileResetPassword(payload.cognitoId);
  return { status: data.status ?? 200, data };
}

/** Lock/unlock account using existing API function. */
async function lockAccountApi(payload: ActionPayload): Promise<ActionResponse> {
  if (!payload.zaxbysId) {
    return { status: 400, data: { status: 400, value: null, error: "Missing zaxbysId", message: null, refreshToken: null } };
  }
  const data = await masterProfileLockRemoveUpdate({ 
    zaxbysId: payload.zaxbysId,
  });
  return { status: data?.status ?? 200, data: data ?? { status: 200, value: null, error: null, message: null, refreshToken: null } };
}

/** Generate and download Right to Know report as Excel spreadsheet. */
async function rightToKnowApi(payload: ActionPayload): Promise<ActionResponse> {
  if (!payload.mpId) {
    return { status: 400, data: { status: 400, value: null, error: "Missing mpId", message: null, refreshToken: null } };
  }
  const result = await masterProfileRightToKnowReport(payload.mpId, payload.customerName);
  return {
    status: result.status,
    data: { status: result.status, value: null, error: null, message: result.message, refreshToken: null },
  };
}

// =============================================================================
// ACTION CONFIGURATIONS
// =============================================================================

const MASTER_PROFILE_ACTIONS: ActionConfig[] = [
  {
    key: "updateAccount",
    label: "Update Account",
    description: "Are you sure you want to update this account? This will save all changes made to the profile.",
    confirmText: "Update",
    apiCall: updateAccountApi,
  },
  {
    key: "toggleAccountStatus",
    label: "Toggle Account Status",
    description: "Are you sure you want to change this account's status?",
    confirmText: "Confirm",
    apiCall: toggleAccountStatusApi,
  },
  {
    key: "privacyDelete",
    label: "Privacy Delete",
    description: "WARNING: This action will permanently delete user data in compliance with privacy regulations. This cannot be undone.",
    confirmText: "Delete",
    apiCall: privacyDeleteApi,
  },
  {
    key: "dnss",
    label: "DNSS",
    description: "Are you sure you want to apply DNSS (Do Not Sell/Share) to this account?",
    confirmText: "Apply DNSS",
    apiCall: dnssApi,
  },
  {
    key: "resetPassword",
    label: "Reset Password",
    description: "This will send a password reset email to the user. Are you sure you want to proceed?",
    confirmText: "Reset",
    apiCall: resetPasswordApi,
  },
  {
    key: "lockAccount",
    label: "Unlock Account",
    description: "Are you sure you want to unlock this account? The user will be able to log in again.",
    confirmText: "Unlock",
    apiCall: lockAccountApi,
  },
  {
    key: "rightToKnow",
    label: "Right to Know Report",
    description: "This will generate and download an Excel spreadsheet containing all stored data for this user. Proceed?",
    confirmText: "Download Report",
    apiCall: rightToKnowApi,
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function isAxiosError(error: unknown): error is AxiosError<Response<unknown>> {
  return (error as AxiosError).isAxiosError === true;
}

function getStatusFromError(error: unknown): number {
  if (isAxiosError(error) && error.response) { return error.response.status; }
  return 500;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const responseData = error.response?.data;
    if (responseData?.error) { return responseData.error; }
    if (responseData?.message) { return String(responseData.message); }
    return error.message;
  }
  if (error instanceof Error) { return error.message; }
  return "An unknown error occurred";
}
function hasNoErrorMessage(data: unknown): boolean {
  const response = data as Response<unknown> | undefined;
  if (response?.error) { return false; }
  if (response?.message && typeof response.message === "string") {
    return !response.message.toLowerCase().includes("error");
  }
  return true;
}

/** Vendor result structure from API responses */
type VendorResult = {
  vendorKey?: string;
  httpCode?: number;
  message?: string;
  error?: string;
};

/** Parse vendor failures from API response - extracts vendors that failed (non-200 status) */
function parseVendorFailures(data: unknown): FormattedError[] {
  const response = data as { 
    errors?: ErrorItem[]; 
    value?: { errors?: ErrorItem[] } | VendorResult[];
  } | VendorResult[] | undefined;
  
  // Check if response is an array of vendor results
  if (Array.isArray(response)) {
    return response
      .filter((item: VendorResult) => item.httpCode && item.httpCode !== 200)
      .map((item: VendorResult): FormattedError => ({
        displayName: item.vendorKey ?? "Unknown Vendor",
        errorMessage: item.message ?? item.error ?? `Failed with status ${item.httpCode}`,
      }));
  }
  
  // Check if value contains vendor results
  if (response?.value && Array.isArray(response.value)) {
    return response.value
      .filter(
        (item): item is VendorResult =>
          item !== null &&
          typeof item === "object" &&
          "httpCode" in item &&
          typeof item.httpCode === "number"
      )
      .filter((item) => item.httpCode !== 200)
      .map((item): FormattedError => ({
        displayName: item.vendorKey ?? "Unknown Vendor",
        errorMessage: item.message ?? item.error ?? `Failed with status ${item.httpCode}`,
      }));
  }
  
  // Fallback to legacy error parsing
  const errors = response?.errors ?? (response?.value as { errors?: ErrorItem[] })?.errors ?? [];
  return errors.map((item): FormattedError => {
    if (typeof item === "string") { 
      return { displayName: item }; 
    }
    return { 
      displayName: item.name ?? item.id ?? JSON.stringify(item), 
      errorMessage: item.error 
    };
  });
}

/** Format failed vendors into a readable string for snackbar */
function formatFailedVendors(errors: FormattedError[]): string {
  if (errors.length === 0) {
    return "";
  }
  const vendorNames = errors.map(e => e.displayName).join(", ");
  return vendorNames;
}

function handleErrorStatus(status: number, label: string, errorMessage: string, showSnackbar: (msg: string, sev: "error") => void) {
  const messages: Record<number, string> = {
    400: `Unable to complete ${label.toLowerCase()}. Please check your information and try again.`,
    401: "Your session has expired. Please log in again to continue.",
    403: "You don't have permission to perform this action. Please contact your administrator if you believe this is an error.",
    404: `The account or resource could not be found. It may have been deleted or moved.`,
    408: `The request took too long to complete. Please check your connection and try again.`,
    409: `Unable to complete ${label.toLowerCase()} due to a conflict. The data may have been modified by another user.`,
    422: `The information provided is invalid. Please review your entries and try again.`,
    429: "Too many requests. Please wait a moment before trying again.",
    500: "We encountered an unexpected error. Our team has been notified. Please try again later.",
    502: "The service is temporarily unavailable. Please try again in a few minutes.",
    503: "The service is currently undergoing maintenance. Please try again shortly.",
    504: "The request timed out. The server is taking too long to respond. Please try again later.",
  };
  
  // Use specific message if available, otherwise show a friendly generic message
  const userMessage = messages[status] ?? 
    `We couldn't complete the ${label.toLowerCase()}. Please try again or contact support if the problem persists.`;
  
  showSnackbar(userMessage, "error");
}

// =============================================================================
// HOOK OPTIONS & RETURN TYPES
// =============================================================================

export type UseMasterProfileActionsOptions = {
  getPayload: () => ActionPayload;
  onSuccess?: (actionKey: MasterProfileActionKey, data: unknown) => void | Promise<void>;
  onPartialSuccess?: (actionKey: MasterProfileActionKey, data: unknown, errors: FormattedError[]) => void | Promise<void>;
  onFailure?: (actionKey: MasterProfileActionKey, data: unknown) => void;
};

/** Override options for dynamic action labels */
export type ActionOverrides = {
  label?: string;
  description?: string;
  confirmText?: string;
};

export type UseMasterProfileActionsReturn = {
  dialogOpen: boolean;
  currentAction: CurrentAction;
  loading: boolean;
  errors: FormattedError[];
  openDialog: (actionKey: MasterProfileActionKey, overrides?: ActionOverrides) => void;
  closeDialog: () => void;
  confirmAction: () => Promise<void>;
  clearErrors: () => void;
};

// =============================================================================
// HOOK
// =============================================================================

export const useMasterProfileActions = ({
  getPayload,
  onSuccess,
  onPartialSuccess,
  onFailure,
}: UseMasterProfileActionsOptions): UseMasterProfileActionsReturn => {
  const { showSnackbar } = useAppSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentActionKey, setCurrentActionKey] = useState<MasterProfileActionKey | null>(null);
  const [actionOverrides, setActionOverrides] = useState<ActionOverrides | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormattedError[]>([]);

  const actionsMap = useMemo(() => {
    const map = new Map<MasterProfileActionKey, ActionConfig>();
    MASTER_PROFILE_ACTIONS.forEach((action) => map.set(action.key, action));
    return map;
  }, []);

  const currentAction = useMemo((): CurrentAction => {
    if (!currentActionKey) { return null; }
    const config = actionsMap.get(currentActionKey);
    if (!config) { return null; }
    return { 
      key: config.key, 
      label: actionOverrides?.label ?? config.label, 
      description: actionOverrides?.description ?? config.description, 
      confirmText: actionOverrides?.confirmText ?? config.confirmText ?? "Confirm" 
    };
  }, [currentActionKey, actionsMap, actionOverrides]);

  const openDialog = useCallback((actionKey: MasterProfileActionKey, overrides?: ActionOverrides) => {
    setCurrentActionKey(actionKey);
    setActionOverrides(overrides ?? null);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    if (loading) { return; }
    setDialogOpen(false);
    setCurrentActionKey(null);
    setActionOverrides(null);
  }, [loading]);

  const clearErrors = useCallback(() => { setErrors([]); }, []);

  const confirmAction = useCallback(async () => {
    if (!currentActionKey || loading) { return; }
    const actionConfig = actionsMap.get(currentActionKey);
    if (!actionConfig) { return; }
    const { label, apiCall } = actionConfig;
    setLoading(true);

    try {
      const response = await apiCall(getPayload());
      const { status, data } = response;

      if (status === 200 && hasNoErrorMessage(data)) {
        showSnackbar(`${label} successful`, "success");
        await onSuccess?.(currentActionKey, data);
      } else if (status === 200) {
        showSnackbar(`${label} failed`, "error");
        onFailure?.(currentActionKey, data);
      } else if (status === 206) {
        const parsedErrors = parseVendorFailures(data);
        setErrors(parsedErrors);
        const failedVendors = formatFailedVendors(parsedErrors);
        const failureMessage = failedVendors 
          ? `${label} partially successful. Failed vendors: ${failedVendors}`
          : `${label} partially successful, but some vendors failed`;
        showSnackbar(failureMessage, "warning");
        await onPartialSuccess?.(currentActionKey, data, parsedErrors);
      } else if (status >= 400) {
        const errorMsg = (data as Response<unknown>)?.error ?? (data as Response<unknown>)?.message;
        const message = errorMsg ? `${label} failed: ${errorMsg}` : `${label} failed`;
        showSnackbar(message, "error");
        onFailure?.(currentActionKey, data);
      } else {
        showSnackbar(`${label} completed`, "success");
        await onSuccess?.(currentActionKey, data);
      }
    } catch (error) {
      handleErrorStatus(getStatusFromError(error), label, getErrorMessage(error), showSnackbar);
      onFailure?.(currentActionKey, { error });
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setCurrentActionKey(null);
      setActionOverrides(null);
    }
  }, [currentActionKey, loading, actionsMap, getPayload, showSnackbar, onSuccess, onPartialSuccess, onFailure]);

  return { dialogOpen, currentAction, loading, errors, openDialog, closeDialog, confirmAction, clearErrors };
};

export default useMasterProfileActions;
